import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import {
  AUTO_ADVANCE_DELAY_MS,
  CONFETTI_PIECES,
  SECTION_COMPLETE_PROGRESS_COLOR,
  sectionCompleteConfettiPNG,
  sectionCompleteConfettiWEBP,
} from '~/pages/anatomyV2/libs/components/SectionCompleteTemplate/constants'
import { SectionCompleteTemplateStyles as S } from '~/pages/anatomyV2/libs/components/SectionCompleteTemplate/styles'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { TPageProps } from '~/shared/types/commonTypes'

interface ISectionCompleteTemplateProps extends TPageProps {
  nextSectionNumber: number
  titleI18nKey: string
  subtitleI18nKey: string
  pointsEarned: number
  pointsGoal: number
  question: string
}

const SectionCompleteTemplate = ({
  pageId,
  nextPagePath,
  nextSectionNumber,
  titleI18nKey,
  subtitleI18nKey,
  pointsEarned,
  pointsGoal,
  question,
}: ISectionCompleteTemplateProps) => {
  useMatchHeaderBackground()

  const [loadingProgress, setLoadingProgress] = useState(0)

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question,
  })

  useEffect(() => {
    const frame = requestAnimationFrame(() => setLoadingProgress(pointsGoal))

    return () => cancelAnimationFrame(frame)
  }, [pointsGoal])

  useEffect(() => {
    const timer = setTimeout(
      () => handleContinue('continue'),
      AUTO_ADVANCE_DELAY_MS,
    )

    return () => clearTimeout(timer)
  }, [handleContinue])

  return (
    <S.Root>
      <S.ContentWrapper>
        <SectionProgress currentSection={nextSectionNumber} dimCurrent />

        <S.CelebrationContent>
          <S.ConfettiWrapper>
            {CONFETTI_PIECES.map((piece, index) => (
              <S.ConfettiPiece
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                $color={piece.color}
                $delay={piece.delay}
                $duration={piece.duration}
                $left={piece.left}
                $rotation={piece.rotation}
              />
            ))}
          </S.ConfettiWrapper>

          <picture>
            <source srcSet={sectionCompleteConfettiWEBP} type="image/webp" />
            <S.Image src={sectionCompleteConfettiPNG} alt="" />
          </picture>

          <S.Title>
            <Trans ns="anatomyV2" i18nKey={titleI18nKey} />
          </S.Title>

          <S.Subtitle>
            <Trans ns="anatomyV2" i18nKey={subtitleI18nKey} />
          </S.Subtitle>

          <S.Points>
            <Trans
              ns="anatomyV2"
              i18nKey="onboarding.sectionComplete.points"
              values={{ count: pointsEarned }}
            />
          </S.Points>

          <S.ProgressWrapper>
            <S.AnimatedProgress
              color={SECTION_COMPLETE_PROGRESS_COLOR}
              $background="transparent"
              $valueBorderRadius="999px"
              $roundedAllSides
              value={loadingProgress}
              max={pointsGoal}
            />
          </S.ProgressWrapper>
        </S.CelebrationContent>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default SectionCompleteTemplate
